import { useEffect, useState } from 'react';
import seedDict from '../../data/music-dictionary.seed.json';
import { fetchDictionary } from '../../cloud/music-api';
import { loadCachedDictionary, saveCachedDictionary } from '../../music/dictionary-cache';
import type { MusicDictionary } from '../../music/types';

const REFRESH_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

export interface UseMusicDictionaryState {
    loading: boolean;
    dictionary: MusicDictionary | null;
}

export function useMusicDictionary(): UseMusicDictionaryState {
    const [state, setState] = useState<UseMusicDictionaryState>({
        loading: true,
        dictionary: null,
    });

    useEffect(() => {
        let cancelled = false;

        (async () => {
            const cached = await loadCachedDictionary();
            const initial = cached ?? (seedDict as MusicDictionary);
            if (!cancelled) setState({ loading: false, dictionary: initial });

            const needsRefresh = !cached || Date.now() - cached.generated_at * 1000 > REFRESH_INTERVAL_MS;
            if (!needsRefresh) return;

            try {
                const fresh = await fetchDictionary(initial.version);
                if (fresh && !cancelled) {
                    await saveCachedDictionary(fresh);
                    setState({ loading: false, dictionary: fresh });
                }
            } catch {
                // Keep seed/cached dictionary on network failures.
            }
        })();

        return () => { cancelled = true; };
    }, []);

    return state;
}
